<script setup lang="ts">
import {computed, onMounted, ref, watch} from 'vue'
import {
  itemScopeAttributes,
  useModelStrings,
  type DetailTemplateProps, ModelList,
} from '@jantm/model-core'
import type {Relation} from "@/types/Wiser/relation.ts";
import {apiClient} from "@/api/http.ts";
import {string} from "zod";
import DocumentsComponent from "@/components/DocumentsComponent.vue";

const props = withDefaults(defineProps<DetailTemplateProps<Relation>>(), { headingLevel: 2 })

const { t, core, locale, detailLabel } = useModelStrings(() => props.definition)

const scope = computed(() => itemScopeAttributes(props.definition))
const heading = computed(() => `h${props.headingLevel}` as const)
const regionLabel = computed(() => props.ariaLabel ?? detailLabel.value)
</script>

<template>
  <section
    class="wiser-detail relation-detail"
    role="region"
    :aria-label="regionLabel"
    :aria-busy="loading && Boolean(entity)"
    v-bind="scope"
  >
    <p
      v-if="error"
      class="wiser-detail__error relation-detail__error"
      role="alert"
    >
      {{ core('state.error', { fallback: 'Something went wrong.' }) }}
    </p>
    <p
      v-else-if="loading && !entity"
      role="status"
    >
      {{ core('state.loading', { fallback: 'Loading…' }) }}
    </p>
    <p
      v-else-if="!entity"
      role="status"
    >
      {{ core('state.notFound', { fallback: 'Not found.' }) }}
    </p>

    <template v-else>
      <!--
        The brand and model together are the accessible name of this record, so
        they are the heading — not decoration above it. The heading level comes
        from the caller so the page outline stays intact wherever it is used.
      -->
      <component
        :is="heading"
        class="wiser-detail__title relation-detail__title"
      >
        <span>{{ entity.title }}</span>
        <span class="wiser-detail__badge relation-detail__badge">{{ entity.relatietype }}</span>
      </component>

      <div class="wiser-detail__description relation-detail__description">
        <h3>Contactgegevens</h3>
        <dl class="wiser-detail__field relation-detail__field" v-if="entity.bedrijfsnaam">
          <dt>{{ t('model.relation.field.name', { fallback: 'Bedrijfsnaam' }) }}</dt>
          <dd>
            <span
              class="wiser-detail__field relation-detail__field"
              itemprop="identifier"
            >{{ entity.bedrijfsnaam }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field relation-detail__field" v-if="entity.adres || entity.huisnummer || entity.toevoeging || entity.postcode || entity.plaats">
          <dt>{{ t('model.relation.field.adres', { fallback: 'Adres' }) }}</dt>
          <dd>
            <span>{{ entity.adres }} {{ entity.huisnummer }} {{ entity.toevoeging }}, {{ entity.postcode }} {{ entity.plaats }}</span>
            <span v-if="entity.land && 'NL' !== entity.land">[{{ entity.land }}]</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field relation-detail__field" v-if="entity.aanhef || entity.voorletters || entity.voornaam || entity.tussenvoegsel || entity.achternaam">
          <dt>{{ t('model.relation.field.name', { fallback: 'Naam' }) }}</dt>
          <dd>
            <span
              class="wiser-detail__field relation-detail__field"
            >{{ entity.aanhef ?? '' }} {{ entity.voorletters ?? '' }} ({{ entity.voornaam ?? '' }}) {{ entity.tussenvoegsel ?? '' }} {{ entity.achternaam ?? '' }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field relation-detail__field" v-if="entity.telefoonnr">
          <dt>{{ t('model.relation.field.telefoonnr', { fallback: 'Telefoon' }) }}</dt>
          <dd>
            <span>{{ entity.telefoonnr }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field relation-detail__field" v-if="entity.gsmnr">
          <dt>{{ t('model.relation.field.gsmnr', { fallback: 'Mobiel' }) }}</dt>
          <dd>
            <span>{{ entity.gsmnr }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field relation-detail__field" v-if="entity?.geboortedatum">
          <dt>{{ t('model.relation.field.geboortedatum', { fallback: 'Geboortedatum' }) }}</dt>
          <dd>
            <span>{{ entity.geboortedatum }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field relation-detail__field" v-if="entity?.geboorteplaats">
          <dt>{{ t('model.relation.field.geboorteplaats', { fallback: 'Geboorteplaats' }) }}</dt>
          <dd>
            <span>{{ entity.geboorteplaats }}</span>
          </dd>
        </dl>
      </div>

      <div class="wiser-detail__description relation-detail__description" v-if="true">
        <h3>Aanvullende gegevens</h3>
        <dl class="wiser-detail__field relation-detail__field" v-if="entity.vestiging">
          <dt>{{ t('model.relation.field.vestiging', { fallback: 'Vestiging' }) }}</dt>
          <dd>
            <span
              class="wiser-detail__field relation-detail__field"
              itemprop="identifier"
            >{{ entity.vestiging }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field relation-detail__field" v-if="entity.klantstatus">
          <dt>{{ t('model.relation.field.klantstatus', { fallback: 'klantstatus' }) }}</dt>
          <dd>
            <span
              class="wiser-detail__field relation-detail__field"
              itemprop="identifier"
            >{{ entity.klantstatus }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field relation-detail__field" v-if="entity.accountmanager">
          <dt>{{ t('model.relation.field.accountmanager', { fallback: 'accountmanager' }) }}</dt>
          <dd>
            <span
              class="wiser-detail__field relation-detail__field"
              itemprop="identifier"
            >{{ entity.accountmanager }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field relation-detail__field" v-if="entity.branche">
          <dt>{{ t('model.relation.field.branche', { fallback: 'branche' }) }}</dt>
          <dd>
            <span
              class="wiser-detail__field relation-detail__field"
              itemprop="identifier"
            >{{ entity.branche }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field relation-detail__field" v-if="entity.betalingsconditie">
          <dt>{{ t('model.relation.field.betalingsconditie', { fallback: 'betalingsconditie' }) }}</dt>
          <dd>
            <span
              class="wiser-detail__field relation-detail__field"
              itemprop="identifier"
            >{{ entity.betalingsconditie }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field relation-detail__field" v-if="entity.geboorteplaats">
          <dt>{{ t('model.relation.field.geboorteplaats', { fallback: 'geboorteplaats' }) }}</dt>
          <dd>
            <span
              class="wiser-detail__field relation-detail__field"
              itemprop="identifier"
            >{{ entity.geboorteplaats }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field relation-detail__field" v-if="entity.emailfinancieel">
          <dt>{{ t('model.relation.field.emailfinancieel', { fallback: 'emailfinancieel' }) }}</dt>
          <dd>
            <span
              class="wiser-detail__field relation-detail__field"
              itemprop="identifier"
            >{{ entity.emailfinancieel }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field relation-detail__field" v-if="entity.bedrijfsgrootte">
          <dt>{{ t('model.relation.field.bedrijfsgrootte', { fallback: 'bedrijfsgrootte' }) }}</dt>
          <dd>
            <span
              class="wiser-detail__field relation-detail__field"
              itemprop="identifier"
            >{{ entity.bedrijfsgrootte }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field relation-detail__field" v-if="entity.opsturenuitslag">
          <dt>{{ t('model.relation.field.opsturenuitslag', { fallback: 'opsturenuitslag' }) }}</dt>
          <dd>
            <span
              class="wiser-detail__field relation-detail__field"
              itemprop="identifier"
            >{{ entity.opsturenuitslag }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field relation-detail__field" v-if="entity.blokkeren">
          <dt>{{ t('model.relation.field.blokkeren', { fallback: 'blokkeren' }) }}</dt>
          <dd>
            <span
              class="wiser-detail__field relation-detail__field"
              itemprop="identifier"
            >{{ entity.blokkeren }}</span>
          </dd>
        </dl>

        <dl class="wiser-detail__field relation-detail__field" v-if="entity.twinfield_id">
          <dt>{{ t('model.relation.field.twinfield_id', { fallback: 'twinfield_id' }) }}</dt>
          <dd>
            <span
              class="wiser-detail__field relation-detail__field"
              itemprop="identifier"
            >{{ entity.twinfield_id }}</span>
          </dd>
        </dl>
      </div>

      <div class="wiser-detail__description relation-detail__description" v-if="entity.opmerkingen || entity.contactruimte || entity.afspraken || entity.financieel">
        <h3>Opmerkingen</h3>
        <dl class="wiser-detail__field relation-detail__field" v-if="entity.opmerkingen">
          <dt>{{ t('model.relation.field.name', { fallback: 'Opmerkingen' }) }}</dt>
          <dd v-html="entity.opmerkingen"></dd>
        </dl>
        <dl class="wiser-detail__field relation-detail__field" v-if="entity.contactruimte">
          <dt>{{ t('model.relation.field.name', { fallback: 'Contactruimte' }) }}</dt>
          <dd v-html="entity.contactruimte"></dd>
        </dl>
        <dl class="wiser-detail__field relation-detail__field" v-if="entity.afspraken">
          <dt>{{ t('model.relation.field.name', { fallback: 'Afspraken' }) }}</dt>
          <dd v-html="entity.afspraken"></dd>
        </dl>
        <dl class="wiser-detail__field relation-detail__field" v-if="entity.financieel">
          <dt>{{ t('model.relation.field.name', { fallback: 'Financieel' }) }}</dt>
          <dd v-html="entity.financieel"></dd>
        </dl>
        <dl class="wiser-detail__field relation-detail__field" v-if="entity.uitzonderingen">
          <dt>{{ t('model.relation.field.uitzonderingen', { fallback: 'uitzonderingen' }) }}</dt>
          <dd v-html="entity.uitzonderingen"></dd>
        </dl>
        <dl class="wiser-detail__field relation-detail__field" v-if="entity.aftersalesnieuweklant">
          <dt>{{ t('model.relation.field.aftersalesnieuweklant', { fallback: 'aftersalesnieuweklant' }) }}</dt>
          <dd v-html="entity.aftersalesnieuweklant"></dd>
        </dl>
      </div>

      <div class="wiser-detail__description relation-detail__description" v-if="entity.adresonderrelatie?.length ?? 0 > 0">
        <h3>Adressen</h3>
        <ModelList class="model-list model-list-address" model="Address" :filter="{page: '1', perPage: '250', filters: encodeURI(`inverse-adresonderrelatie=${encodeURIComponent(String(entity.id))}`)}" />
      </div>

      <div class="wiser-detail__description relation-detail__description" v-if="entity.contactonderrelatie?.length ?? 0 > 0">
        <h3>Contacten</h3>
        <ModelList class="model-list model-list-contact-person" model="ContactPerson" :filter="{page: '1', perPage: '250', filters: encodeURI(`inverse-contactonderrelatie=${encodeURIComponent(String(entity.id))}`)}" />
      </div>

      <div class="wiser-detail__description relation-detail__description" v-if="(entity.factuuronderrelatie?.length ?? 0 > 0) || (entity.workbasefacturen?.length ?? 0 > 0)">
        <h3>Facturen</h3>
        <ModelList class="model-list model-list-invoice" model="Invoice" :filter="{page: '1', perPage: '250', filters: encodeURI(`inverse-factuuronderrelatie=${encodeURIComponent(String(entity.id))}`)}" />
<!-- TODO: Voeg historische facturen toe  //-->
<!--        <ModelList class="model-list model-list-workbase-invoice" model="WorkbaseInvoice" :filter="{page: '1', perPage: '250', filters: encodeURI(`inverse-historischefactuuronderrelatie=${encodeURIComponent(String(entity.id))}`)}" />-->
      </div>

      <div class="wiser-detail__description relation-detail__description" v-if="entity.offerteonderrelatie?.length ?? 0 > 0">
        <h3>Offertes</h3>
        <ModelList class="model-list model-list-offer" model="Offer" :filter="{page: '1', perPage: '250', filters: encodeURI(`inverse-offerteonderrelatie=${encodeURIComponent(String(entity.id))}`)}" />
      </div>

      <div class="wiser-detail__description relation-detail__description" v-if="entity?.relatietype !== 'bedrijf'">
        <h3>Certificaten</h3>
        <ModelList class="model-list model-list-offer" model="Certificate" :filter="{page: '1', perPage: '250', filters: encodeURI(`student=${encodeURIComponent(String(entity.id))}`)}" />
      </div>
      <div class="wiser-detail__description relation-detail__description" v-else>
        <h3>Certificaten</h3>
        <ModelList class="model-list model-list-offer" model="Certificate" :filter="{page: '1', perPage: '250', filters: encodeURI(`company=${encodeURIComponent(String(entity.id))}`)}" />
      </div>

      <DocumentsComponent :entityId="entity.id" />
    </template>
  </section>
</template>
